import { useEffect, useState, useCallback } from "react";
import API from "../api";
import { dataGridStyle } from "../theme";
import { GridToolbarBar, ToolbarGroup, ColumnsButton, ExportCsvButton } from "./ToolbarKit";
import { DataGridPro, GridFilterInputValue, GridLogicOperator } from "@mui/x-data-grid-pro";
import DateRangeFilter from "./dateRangeFilter";
import {
  TOEstablishTheColorOfLog,
  handleCellIPClick,
  hasExternalIp,
} from "./ImportantFunctions";
import { Box, Grid, Typography, Tooltip } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import BackupIcon from "@mui/icons-material/Backup";



// Stessa toolbar delle tabelle Security (ToolbarKit), senza filtri.
const CustomToolbar = () => (
  <GridToolbarBar>
    <ToolbarGroup>
      <ColumnsButton />
      <ExportCsvButton options={{ fileName: "tiresia-logs" }} />
    </ToolbarGroup>
  </GridToolbarBar>
);

function DetailPanelContent({ row }) {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const transformData = row.logs.map((item, index) => ({
      id: index,
      ...item
    }))
    setDetails(transformData)
    setLoading(false)
  }, [row])


  return (

    <Box padding={2} sx={{ backgroundColor: "#333333", height: 300 }}>
      <DataGridPro
        sx={{
          fontWeight: 900,
          height: 250,
          "& .MuiDataGrid-root": {
            border: "none", // Rimuove il bordo intorno alla griglia
            backgroundColor: "transparent !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none", // Rimuove il bordo inferiore delle celle
            padding: "8px 16px", // Imposta un padding per le celle
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent", // Rende trasparente lo scroller virtuale
          },
          "& .MuiDataGrid-footerContainer": {
            display: "none", // Nasconde il footer della tabella
          },
          "& .MuiDataGrid-toolbarContainer": {
            display: "none", // Nasconde la toolbar della tabella
          },
        }}
        rows={details}
        loading={loading}
        columns={[

          { field: 'timeStamp', headerName: 'TIMESTAMP', flex: 1 },
          {
            field: "portaSorgente",
            headerName: "SOURCE PORT",
            flex: 1,
            headerAlign: "left",
            align: "left",
          },
          {
            field: "portaDestinatario",
            headerName: "DESTINATION PORT",
            flex: 1,
            headerAlign: "left",
            align: "left",
          },
          {
            field: "score",
            headerName: "SCORE",
            headerAlign: "center",
            flex: 1,
            minWidth: 100,
            align: "center",
            renderCell: (params) => {
              return (
                <Typography sx={{ fontSize: 15, fontWeight: 900, color: params.value <= 0.89 ? "#8963b2" : params.value <= 0.98 ? "orange" : "red" }}>{(params.value * 100).toFixed(2)} %</Typography>
              )
            }
          },
        ]}
      />
    </Box>
  );
}

const TiresiaDataGrid = ({ tipo, reseller = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const timeZone = window.sessionStorage.getItem("timezone");
  const [levelT, setLevelT] = useState(0);
  const [hideBackup] = useState(false);
  const [periodFrom, setPeriodFrom] = useState('Not Found')

  const filteredData = hideBackup
    ? data.filter((row) => !row.backup) // Escludi righe con `backup: true`
    : data;



  useEffect(() => {
    API.getTiresiaLevel().then((res) => {
      setLevelT(res.data.level);
    });
  }, []);

  useEffect(() => {
    API.getTiresia({ tiresiaSecurity: tipo })
      .then((res) => {
        const transformedData = res.data.log.map((item, index) => ({
          id: index,
          ...item,
        }));
        setData(transformedData);
        setPeriodFrom(res.data.periodFrom)
        setLoading(false);
      })
      .catch((err) => {
        console.error('[TiresiaDataGrid] Failed to fetch Tiresia data:', err);
        setLoading(false);
      });
  }, [tipo]);

  const columns = [
    {
      field: "lastTimeStamp",
      headerName: `LAST TIMESTAMP (${timeZone ? timeZone : "GMT"})`,
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      type: "date",
      filterable: false,
      valueGetter: (value) => {
        return value ? value : null;
      },
      valueFormatter: (value) => {
        if (!value) {
          return "";
        }
        const valueFormatted = new Date(value).toLocaleString();
        return `${valueFormatted}`;
      },
    },
    {
      field: "ipSorgente",
      headerName: "SOURCE",
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            className="clickable-cell"
            style={{
              width: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              gap: 20,
            }}
            onMouseEnter={(e) => {
              e.target.innerText = params.value.toString();
            }}
            onMouseLeave={(e) => {
              e.target.innerText =
                params.row.hostNameAtt || params.value.toString();
            }}
          >
            {params.row.hostNameAtt || params.value.toString()}
          </div>
        );
      },
    },
    {
      field: "ipDestinatario",
      headerName: "DESTINATION",
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            className="clickable-cell"
            style={{
              width: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              gap: 20,
            }}
            onMouseEnter={(e) => {
              e.target.innerText = params.value.toString();
            }}
            onMouseLeave={(e) => {
              e.target.innerText =
                params.row.hostNameDest || params.value.toString();
            }}
          >
            {params.row.hostNameDest || params.value.toString()}
          </div>
        );
      },
    },
    {
      field: "dettaglio",
      headerName: "DETAILS",
      flex: 1,
      minWidth: 280,
      headerAlign: "left",
      align: "left",
      type: "string", // Specifica il tipo di colonna come 'date'
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            className="detail-clickable-cell"
            style={{
              color: TOEstablishTheColorOfLog(params.value),
              width: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              gap: 20,
            }}
          >
            {params.value}
            {hasExternalIp(
              params.row.ipDestinatario,
              params.row.ipSorgente
            ) && (
                <Tooltip
                  title={
                    <Typography sx={{ fontSize: 15, color: "orange" }}>
                      External Connection
                    </Typography>
                  }
                  arrow
                >
                  <LanguageIcon />
                </Tooltip>
              )}
            {params.row.backup && (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 15, color: "#61a9ad" }}>
                    Possible Backup
                  </Typography>
                }
                arrow
              >
                <BackupIcon />
              </Tooltip>
            )}
          </div>
        );
      },
      filterOperators: [
        {
          label: "equals",
          value: "equals",
          getApplyFilterFn: () => null,
          InputComponent: GridFilterInputValue,
        },
        {
          label: "contains",
          value: "contains",
          getApplyFilterFn: () => null,
          InputComponent: GridFilterInputValue,
        },
      ],
    },
    {
      field: "count",
      headerName: "COUNT",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
      filterable: false
    },
  ];


  const getDetailPanelContent = useCallback(
    ({ row }) => <DetailPanelContent row={row} />,
    [],
  );

  const getDetailPanelHeight = useCallback(() => 300, []);

  return (
    <Box
      m="40px 0 0 0"
      height="80vh"
    >
      <Grid container spacing={2}>
        <Grid
          height={"80vh"}
          item
          md={12}
          sx={{
            ...(levelT < 37 && {
              filter: "blur(5px)",
              pointerEvents: "none", // Impedisce interazioni durante il blur
            }),
          }}
        >
          {!loading && <Typography sx={{mb: 3, fontSize: 18, fontWeight: 900}}>Log from {periodFrom} to Today</Typography>}
          <Box sx={{ height: "calc(100% - 50px)" }}>
            <DataGridPro
              sx={dataGridStyle}
              showToolbar
              rows={filteredData}
              pageSizeOptions={[100]}
              localeText={{
                noRowsLabel: "No data in the set dates",
              }}
              getRowClassName={(params) =>
                params.row.backup ? "backupRow" : ""
              }
              pagination
              autoPageSize
              columns={columns}
              slots={{
                filterPanel: null,
                toolbar: CustomToolbar, // Usa la tua toolbar personalizzata

              }}
              loading={loading}
              onCellClick={(params) => {
                handleCellIPClick(
                  params,
                  "security",
                  reseller ? reseller : false
                );
              }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                },
                filterPanel: {
                  logicOperators: [GridLogicOperator.And],
                },
              }}
              componentsProps={{
                filterPanel: {
                  FilterComponent: DateRangeFilter,
                  sx: {
                    "& .MuiDataGrid-filterForm": {
                      minHeight: "500px",
                    },
                    "& .MuiDataGrid-filterForm--closed": {
                      minHeight: "5000px",
                    },
                  },
                },
              }}
              getDetailPanelHeight={getDetailPanelHeight}
              getDetailPanelContent={getDetailPanelContent}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TiresiaDataGrid;
